import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Bot, ArrowRight, Clock, Users, ClipboardList, BarChart3, Shield, HelpCircle, Settings, SunMedium, History, Bell, CreditCard, TrendingUp, AlertTriangle, CheckCircle, Zap, Lightbulb, Loader2 } from 'lucide-react';
import { API } from '../../config/apiBase';


export default function CopilotPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const isEmpresa = user?.account_type === 'empresa' || user?.account_type === 'client';
    const isColaborador = user?.account_type === 'colaborador' || user?.account_type === 'client_user';

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token || !isEmpresa) { setLoading(false); return; }
        const h = { 'Authorization': `Bearer ${token}` };
        Promise.all([
            fetch(`${API}/ponto/dashboard/extended`, { headers: h }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/ponto/empresa`, { headers: h }).then(r => r.ok ? r.json() : null),
        ]).then(([s, e]) => {
            setStats({ ...s, plan: e?.plan_slug, plan_status: e?.plan_status });
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [isEmpresa]);

    // Smart insights based on real data
    const getInsights = () => {
        if (!stats) return [];
        const insights = [];
        const presRate = stats.total_employees > 0 ? Math.round((stats.present_today / stats.total_employees) * 100) : 0;
        const usage = stats.total_employees > 0 && stats.max_employees > 0 ? Math.round((stats.total_employees / stats.max_employees) * 100) : 0;

        if (usage >= 80) insights.push({ type: 'warning', icon: AlertTriangle, text: `Você está usando ${usage}% das vagas de colaboradores. Considere fazer upgrade do plano.`, action: '/sistema/empresa/assinatura', actionLabel: 'Ver plano' });
        if (presRate > 0 && presRate < 50) insights.push({ type: 'alert', icon: AlertTriangle, text: `Taxa de presença hoje: ${presRate}%. Abaixo do esperado.`, action: '/sistema/ponto', actionLabel: 'Ver ponto' });
        if (presRate >= 80) insights.push({ type: 'success', icon: CheckCircle, text: `Equipe com ${presRate}% de presença hoje. Bom ritmo!`, action: null });
        if ((stats.pending_ajustes || 0) > 0) insights.push({ type: 'warning', icon: Clock, text: `${stats.pending_ajustes} solicitação(ões) de ajuste pendente(s).`, action: '/sistema/empresa/solicitações', actionLabel: 'Revisar' });
        if (stats.total_employees === 0) insights.push({ type: 'info', icon: Users, text: 'Cadastre seu primeiro colaborador para começar a usar o sistema.', action: '/sistema/empresa/colaboradores', actionLabel: 'Cadastrar' });
        if ((stats.total_jornadas || 0) === 0) insights.push({ type: 'info', icon: Clock, text: 'Configure uma jornada de trabalho para organizar os turnos.', action: '/sistema/empresa/escalas', actionLabel: 'Criar jornada' });
        if (insights.length === 0) insights.push({ type: 'success', icon: CheckCircle, text: 'Tudo em ordem! Sua operação está funcionando normalmente.', action: null });
        return insights;
    };

    const empresaCards = [
        { title: 'Como cadastrar um colaborador?', desc: 'Vá em Colaboradores > Cadastrar. Preencha nome, e-mail, senha e cargo.', icon: Users, href: '/sistema/empresa/colaboradores' },
        { title: 'Como revisar o ponto da equipe?', desc: 'Acesse Ponto Digital para ver presentes, ausentes e marcações do dia.', icon: Clock, href: '/sistema/ponto' },
        { title: 'Como aprovar solicitações?', desc: 'Vá em Solicitações/Ajustes e clique em Aprovar ou Recusar.', icon: ClipboardList, href: '/sistema/empresa/solicitacoes' },
        { title: 'Como ver a performance?', desc: 'A aba Performance mostra taxa de presença, registros e indicadores.', icon: BarChart3, href: '/sistema/empresa/performance' },
        { title: 'Como configurar o geofence?', desc: 'Vá em Configurações > Geofence e defina coordenadas e raio.', icon: Settings, href: '/sistema/empresa/configurações' },
        { title: 'Como gerenciar permissões?', desc: 'Acesse Permissões e Acessos para ver e controlar níveis de acesso.', icon: Shield, href: '/sistema/empresa/permissões' },
        { title: 'Como ver meu plano?', desc: 'A aba Assinatura/Plano mostra seu plano atual, limites e vencimento.', icon: CreditCard, href: '/sistema/empresa/assinatura' },
    ];

    const colaboradorCards = [
        { title: 'Como bater ponto?', desc: 'Vá em Ponto Digital, permita a localização e clique em Registrar Entrada ou Saída.', icon: Clock, href: '/sistema/ponto' },
        { title: 'Como ver meu dia?', desc: 'A aba Meu Dia mostra seu status, timeline da jornada e marcações.', icon: SunMedium, href: '/sistema/colaborador/meu-dia' },
        { title: 'Como ver meu histórico?', desc: 'Acesse Meu Histórico para ver todas as marcações por dia/semana/mês.', icon: History, href: '/sistema/colaborador/histórico' },
        { title: 'Como pedir ajuste de ponto?', desc: 'Vá em Solicitações > Nova Solicitação, selecione o motivo e envie.', icon: ClipboardList, href: '/sistema/colaborador/solicitacoes' },
        { title: 'Como ver meus alertas?', desc: 'A aba Alertas mostra avisos do sistema, da empresa e pendências.', icon: Bell, href: '/sistema/alertas' },
        { title: 'Preciso de ajuda', desc: 'Acesse Suporte para abrir um chamado ou consultar perguntas frequentes.', icon: HelpCircle, href: '/sistema/suporte' },
    ];

    const cards = isEmpresa ? empresaCards : colaboradorCards;
    const insights = getInsights();
    const insightColors = { success: '#10B981', warning: '#F59E0B', alert: '#EF4444', info: '#3B82F6' };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="copilot-page">
            {/* Header */}
            <div className="rounded-xl p-5" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.12) 0%, rgba(139,92,246,0.08) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)' }}>
                        <Bot size={20} style={{ color: 'var(--sv-gold-primary)' }} />
                    </div>
                    <div>
                        <h2 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Copilot SuperVitrine</h2>
                        <p className="text-xs" style={{ color: 'var(--muted)' }}>Seu assistente inteligente no sistema</p>
                    </div>
                </div>
                <p className="text-sm mt-2" style={{ color: 'var(--muted)' }}>
                    {isEmpresa ? 'Olá! Aqui estão as sugestões baseadas na operação da sua empresa.' : 'Olá! Vou te ajudar a usar o sistema de forma simples e rápida.'}
                </p>
            </div>

            {/* Smart Insights (only for empresa) */}
            {isEmpresa && insights.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-1">
                        <Lightbulb size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Diagnóstico da operação</h3>
                    </div>
                    {insights.map((ins, i) => (
                        <div key={i} className="flex items-start gap-3 p-3.5 rounded-xl" style={{ background: `${insightColors[ins.type]}08`, border: `1px solid ${insightColors[ins.type]}20` }}>
                            <ins.icon size={16} className="shrink-0 mt-0.5" style={{ color: insightColors[ins.type] }} />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs" style={{ color: 'var(--text)' }}>{ins.text}</p>
                            </div>
                            {ins.action && (
                                <a href={ins.action} className="text-[10px] font-medium shrink-0 px-2.5 py-1 rounded-lg" style={{ background: `${insightColors[ins.type]}12`, color: insightColors[ins.type] }}>
                                    {ins.actionLabel}
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Quick Actions */}
            <div className="space-y-2">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Perguntas Frequentes</h3>
                {cards.map((c) => (
                    <a key={c.title} href={c.href} className="flex items-start gap-3 p-4 rounded-xl transition-all hover:scale-[1.005]"
                        style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`copilot-card-${c.title.slice(0,20)}`}>
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                            <c.icon size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{c.title}</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{c.desc}</p>
                        </div>
                        <ArrowRight size={14} className="shrink-0 mt-1" style={{ color: 'var(--muted)' }} />
                    </a>
                ))}
            </div>

            {/* Help */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.05)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                <p className="text-xs" style={{ color: 'var(--sv-gold-primary)' }}>
                    Não encontrou o que precisa? Acesse o <a href="/sistema/suporte" className="underline font-medium">Suporte</a> para abrir um chamado.
                </p>
            </div>
        </div>
    );
}
